import { useCallback, useState } from 'react';
import { css } from '@linaria/core';
import { TextInput } from '@cbhq/cds-web/controls';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { FullscreenModal } from '@cbhq/cds-web/overlays/Modal/FullscreenModal';
import { Pressable, ThemeProvider } from '@cbhq/cds-web/system';
import { Link, TextDisplay1, TextDisplay3, TextHeadline } from '@cbhq/cds-web/typography';

import { ReactComponent as StarIcon } from './assets/star.svg';
import questions from './questions';

const modalContainer = css`
  margin: 0;
  overflow: hidden;
`;

const starIconBox = css`
  fill: var(--background-inverse);
`;

const inputClass = css`
  border-width: 0;
`;

interface QuestionModalProps {
  visible: boolean;
  handleClose: () => void;
  questionId: string;
}

export function QuestionModal({
  visible,
  handleClose,
  questionId,
}: QuestionModalProps): React.ReactElement {
  const question = questions[questionId];
  const [showHint, setShowHint] = useState(false);

  const handleSubmitPress = useCallback(() => {
    setShowHint(false);
  }, []);

  const primaryContent = (
    <VStack
      width="100vw"
      height="100%"
      alignItems="center"
      position="relative"
      dangerouslySetBackground="rgba(10, 11, 13, 1)"
      gap={8}
    >
      <VStack gap={2} width="50%" spacing={2} position="relative">
        <Box position="absolute" top={24} left={-100} className={starIconBox}>
          <StarIcon />
        </Box>

        {Boolean(question?.title) && <TextDisplay1 as="h1">{question?.title}</TextDisplay1>}

        <TextDisplay3 as="p">{question?.question}</TextDisplay3>
        <HStack spacingTop={8} gap={8}>
          <TextInput
            placeholder="Answer here"
            bordered={false}
            className={inputClass}
            end={
              Boolean(question?.hint) && (
                <Box spacingEnd={2}>
                  <Link variant="caption" color="primary" to="" onPress={() => setShowHint(true)}>
                    Hint
                  </Link>
                </Box>
              )
            }
          />
          <Pressable
            as="a"
            background="backgroundInverse"
            borderRadius="roundedFull"
            onPress={handleSubmitPress}
            width={150}
          >
            <Box spacingHorizontal={3} spacingVertical={2} justifyContent="center">
              <TextHeadline as="p" dangerouslySetColor="var(--background)">
                Submit
              </TextHeadline>
            </Box>
          </Pressable>
        </HStack>
        {showHint && (
          <TextHeadline as="p" dangerouslySetColor="var(--background-inverse)">
            {question?.hint}
          </TextHeadline>
        )}
      </VStack>
    </VStack>
  );

  return (
    <ThemeProvider scale="large" spectrum="dark">
      <FullscreenModal
        hideDivider
        visible={visible}
        logo={<div />}
        dangerouslySetContentClassName={modalContainer}
        onRequestClose={() => {
          handleClose();
          setShowHint(false);
        }}
        primaryContent={primaryContent}
        closeAccessibilityLabel="Close"
      />
    </ThemeProvider>
  );
}
